import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, type PutAwayRowData } from '../../api';
import { ColorSwatch, ItemImage } from '../../components/ItemImage';
import { useLocations } from '../../queries';
import { Button, ErrorMessage, Select, Spinner, number } from '../../ui';

/** Stock with no bin, one part at a time: the suggested bin is one tap, and "somewhere else" is a short list. */
export function MobilePutAway() {
  const qc = useQueryClient();
  const locations = useLocations();
  const [skipped, setSkipped] = useState<Set<number>>(new Set());
  const [other, setOther] = useState('');
  const list = useQuery({ queryKey: ['put-away', null], queryFn: () => api<{ rows: PutAwayRowData[]; total: number }>('/api/v1/put-away') });
  const put = useMutation({
    mutationFn: (m: { entryId: number; locationId: number }) => api('/api/v1/put-away', { body: { moves: [m] } }),
    onSuccess: () => {
      setOther('');
      for (const k of ['put-away', 'lots', 'dashboard', 'locations', 'item-inventory']) void qc.invalidateQueries({ queryKey: [k] });
    },
  });

  if (list.isPending) return <Spinner />;
  if (list.error) return <ErrorMessage error={list.error} />;
  const queue = list.data.rows.filter((r) => !skipped.has(r.entryId));
  const r = queue[0];
  if (!r) {
    return (
      <div className="space-y-3 text-center">
        <p className="mt-10 text-lg font-semibold">{skipped.size > 0 ? 'Only the skipped ones are left.' : 'Nothing to put away.'}</p>
        <p className="text-sm text-slate-600">{skipped.size > 0 ? 'Every other piece has a bin.' : 'Every piece in stock has a bin.'}</p>
        {skipped.size > 0 && (
          <Button large variant="secondary" onClick={() => setSkipped(new Set())}>
            Go through the skipped ones
          </Button>
        )}
      </div>
    );
  }
  const go = (locationId: number) => put.mutate({ entryId: r.entryId, locationId });
  const choices = [r.suggestion, ...r.alternatives].filter((x): x is NonNullable<typeof x> => x != null);
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">
        {number(queue.length)} to go{list.data.total > list.data.rows.length ? ` (${number(list.data.total)} in all)` : ''}
      </p>
      <div className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
        <div className="flex gap-3">
          <ItemImage className="size-24" itemType={r.itemType} itemNo={r.itemNo} colorId={r.colorId} eager />
          <div className="min-w-0">
            <div className="text-lg font-semibold">{r.itemNo}</div>
            <div className="text-sm text-slate-600">{r.itemName}</div>
            <div className="mt-1 text-sm">
              <ColorSwatch rgb={r.colorRgb} name={r.colorName} /> · {r.condition === 'N' ? 'New' : 'Used'}
            </div>
          </div>
        </div>
        <div className="mt-3 text-3xl font-bold tabular-nums">{number(r.qty)} pcs</div>
        <div className="text-xs text-slate-500">{r.source || `Purchase ${r.purchaseId}`}</div>
      </div>

      <ErrorMessage error={put.error} />
      {r.suggestion ? (
        <Button large className="w-full" disabled={put.isPending} onClick={() => go(r.suggestion!.locationId)}>
          Put in {r.suggestion.code}
          <span className="font-normal opacity-80">{r.suggestion.reason === 'holds' ? ` · holds ${number(r.suggestion.qtyThere)}` : ' · usual bin'}</span>
        </Button>
      ) : (
        <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">No bin holds this yet. Choose one below.</p>
      )}
      {choices.slice(1).map((c) => (
        <Button key={c.locationId} large variant="secondary" className="w-full" disabled={put.isPending} onClick={() => go(c.locationId)}>
          Put in {c.code}
          <span className="font-normal opacity-70">{c.reason === 'holds' ? ` · holds ${number(c.qtyThere)}` : ' · usual bin'}</span>
        </Button>
      ))}
      <div className="flex gap-2">
        <Select aria-label="Another bin" className="min-h-12 flex-1 text-base" value={other} onChange={(e) => setOther(e.target.value)}>
          <option value="">Another bin…</option>
          {locations.data?.map((l) => (
            <option key={l.id} value={l.id}>
              {l.code}
            </option>
          ))}
        </Select>
        <Button large disabled={!other || put.isPending} onClick={() => go(Number(other))}>
          Put here
        </Button>
      </div>
      <Button large variant="ghost" className="w-full" onClick={() => setSkipped(new Set([...skipped, r.entryId]))}>
        Skip for now
      </Button>
    </div>
  );
}
